import type { AppTimeEntry } from "@/features/timesheetFlow/timesheetFlowApi";
import type { TimesheetRowRecord } from "@/features/timesheetFlow/timesheetFlowTypes";
import type { SuggestionItem } from "@/features/timesheetFlow/components/SuggestionPickerDialog";

function formatTimePart(value: string | null | undefined): string | null {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const match = raw.match(/(\d{1,2}):(\d{2})/);
  if (!match) return null;
  return `${match[1]!.padStart(2, "0")}:${match[2]}`;
}

export function getAppTimePersonName(entry: AppTimeEntry): string {
  return String(entry.user_display || entry.user_login || "").trim() || "Unbekannt";
}

export function getAppTimeClaimedHours(entry: AppTimeEntry): number {
  const claimed = Number(entry.claimed_hours ?? entry.claimed_hours_decimal ?? 0);
  return Number.isFinite(claimed) && claimed > 0 ? claimed : 0;
}

/** Offene Stunden für den nächsten Stundennachweis (Mehrstunden nach Claim). */
export function getAppTimeOpenHours(entry: AppTimeEntry): number {
  const open = Number(entry.open_hours);
  if (Number.isFinite(open)) return Math.max(0, open);
  const total = Number(entry.hours_decimal);
  if (!Number.isFinite(total)) return 0;
  return Math.max(0, total - getAppTimeClaimedHours(entry));
}

/** Stunden, die beim Übernehmen in die Zeile geschrieben werden. */
export function getAppTimeSuggestHours(
  entry: AppTimeEntry,
  options?: { allowReuseOnThisSheet?: boolean },
): number {
  const openHours = getAppTimeOpenHours(entry);
  if (openHours > 0.001) return openHours;
  if (options?.allowReuseOnThisSheet) {
    const total = Number(entry.hours_decimal);
    return Number.isFinite(total) && total > 0 ? total : 0;
  }
  return 0;
}

export function getAppTimeUsedTimesheetId(
  entry: AppTimeEntry,
  usageById: Map<number, number | null>,
): number | null {
  const id = Number(entry.id);
  if (!Number.isFinite(id)) return null;
  if (usageById.has(id)) return usageById.get(id) ?? null;
  const fromEntry = entry.used_timesheet_id ?? entry.timesheet_id;
  return fromEntry != null ? Number(fromEntry) : null;
}

export function buildAppTimeSuggestionItems(
  items: AppTimeEntry[],
  options: {
    usageById: Map<number, number | null>;
    editableInThisTimesheet: Set<number>;
    locallyFreed: Set<number>;
    usedInCurrentDraft: Set<number>;
    currentTimesheetId: number | null;
  },
): SuggestionItem[] {
  return items.map((it) => {
    const id = Number(it.id);
    const person = getAppTimePersonName(it);
    const dateStr = it.work_date
      ? new Date(`${it.work_date}T00:00:00`).toLocaleDateString("de-DE")
      : "";
    const usedInDraft = options.usedInCurrentDraft.has(id);
    const isLocallyFreed = options.locallyFreed.has(id);
    const allowReuse =
      isLocallyFreed || (options.editableInThisTimesheet.has(id) && !usedInDraft);
    const suggestHours = getAppTimeSuggestHours(it, { allowReuseOnThisSheet: allowReuse });
    const claimed = getAppTimeClaimedHours(it);
    const openHours = getAppTimeOpenHours(it);
    const hoursStr = suggestHours
      ? suggestHours.toFixed(2).replace(".", ",") + " h"
      : "";
    const timeFrom = formatTimePart(it.time_from);
    const timeTo = formatTimePart(it.time_to);
    const rangeStr = timeFrom && timeTo ? `${timeFrom}–${timeTo}` : "";

    const disabled = usedInDraft || suggestHours <= 0.001;
    let disabledReason: string | undefined;
    if (usedInDraft) {
      disabledReason = "Bereits übernommen";
    } else if (claimed > 0 && openHours <= 0.001) {
      disabledReason = "Bereits in Stundennachweis";
    } else if (suggestHours <= 0.001) {
      disabledReason = "Keine offenen Stunden";
    }

    const metaParts: string[] = [];
    const comment = (it.comment || "").trim();
    if (comment) metaParts.push(comment);
    if (claimed > 0 && openHours > 0.001) {
      metaParts.push(`${openHours.toFixed(2).replace(".", ",")} h offen`);
    }

    return {
      id: String(id),
      label: [dateStr, person, rangeStr, hoursStr].filter(Boolean).join(" · "),
      meta: metaParts.length ? metaParts.join(" · ") : undefined,
      disabled,
      disabledReason,
    };
  });
}

export function appTimeEntryToRow(
  entry: AppTimeEntry,
  options?: { allowReuseOnThisSheet?: boolean },
): TimesheetRowRecord {
  const hoursVal = getAppTimeSuggestHours(entry, options);
  return {
    work_date: entry.work_date || "",
    person_name: getAppTimePersonName(entry),
    person_login: entry.user_login || null,
    hours_decimal: Number.isFinite(hoursVal) ? hoursVal : 0,
    time_from: formatTimePart(entry.time_from),
    time_to: formatTimePart(entry.time_to),
    comment: entry.comment || "Arbeitszeit",
    employee_time_id: Number(entry.id),
  };
}

export function importAppTimesToRows(
  items: AppTimeEntry[],
  options: {
    usageById: Map<number, number | null>;
    editableInThisTimesheet: Set<number>;
    existingRows: TimesheetRowRecord[];
    locallyFreed: Set<number>;
    currentTimesheetId: number | null;
  },
): TimesheetRowRecord[] {
  const existing = new Set<number>();
  for (const row of options.existingRows) {
    if (row.employee_time_id != null) {
      existing.add(Number(row.employee_time_id));
    }
  }

  const added: TimesheetRowRecord[] = [];

  for (const it of items) {
    const id = Number(it.id);
    if (!Number.isFinite(id)) continue;

    const isLocallyFreed = options.locallyFreed.has(id);
    if (existing.has(id) && !isLocallyFreed) continue;

    const allowReuse =
      isLocallyFreed || options.editableInThisTimesheet.has(id);
    const suggestHours = getAppTimeSuggestHours(it, { allowReuseOnThisSheet: allowReuse });
    if (suggestHours <= 0.001) continue;

    added.push(appTimeEntryToRow(it, { allowReuseOnThisSheet: allowReuse }));
    existing.add(id);
  }

  return added;
}
